import React, { useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write your content here...',
  className = '',
  disabled = false,
}) => {
  const quillRef = useRef<ReactQuill>(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'script',
    'indent',
    'color', 'background',
    'align',
    'link', 'image', 'video',
    'blockquote', 'code-block',
  ];

  // Handle image upload
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Quill typings for modules can be broad; cast to any to access addHandler safely
      const toolbar = quill.getModule('toolbar') as any;
      
      toolbar.addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
          const file = input.files?.[0];
          if (file) {
            // Create a placeholder image while uploading
            const range = quill.getSelection(true);
            const index = range ? range.index : 0;
            
            // Insert placeholder
            quill.insertEmbed(index, 'image', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
            
            // TODO: Upload image to server and replace with actual URL
            // For now, use a data URL placeholder
            const reader = new FileReader();
            reader.onload = (e) => {
              const dataUrl = e.target?.result as string;
              quill.deleteText(index, 1);
              quill.insertEmbed(index, 'image', dataUrl);
            };
            reader.readAsDataURL(file);
          }
        };
      });
    }
  }, []);

  return (
    <div className={`rich-text-editor ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        readOnly={disabled}
        style={{
          backgroundColor: 'var(--patreon-card-bg)',
          color: 'var(--patreon-black)',
        }}
      />
      <style>{`
        .rich-text-editor .ql-container {
          font-size: 14px;
          font-family: inherit;
          min-height: 200px;
        }
        .rich-text-editor .ql-editor {
          min-height: 200px;
          color: var(--patreon-black);
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #999;
          font-style: normal;
        }
        .rich-text-editor .ql-toolbar {
          border-top: 1px solid var(--patreon-card-border);
          border-left: 1px solid var(--patreon-card-border);
          border-right: 1px solid var(--patreon-card-border);
          border-bottom: none;
          border-radius: 8px 8px 0 0;
          background: #f9fafb;
        }
        .rich-text-editor .ql-container {
          border-bottom: 1px solid var(--patreon-card-border);
          border-left: 1px solid var(--patreon-card-border);
          border-right: 1px solid var(--patreon-card-border);
          border-top: none;
          border-radius: 0 0 8px 8px;
        }
        .rich-text-editor .ql-stroke {
          stroke: var(--patreon-black);
        }
        .rich-text-editor .ql-fill {
          fill: var(--patreon-black);
        }
        .rich-text-editor .ql-picker-label {
          color: var(--patreon-black);
        }
        .rich-text-editor .ql-snow .ql-picker {
          color: var(--patreon-black);
        }
        .rich-text-editor .ql-snow .ql-stroke.ql-thin {
          stroke-width: 1;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;


